import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, HandHelping, Sparkles, Send, MessageCircleQuestion, ChevronDown, ChevronUp } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";

const CATEGORIES = { intro: "Intro", hiring: "Hiring", advice: "Advice", customers: "Customers", other: "Other" };

function AskRow({ ask, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [responses, setResponses] = useState(null);

  const toggle = async () => {
    if (!expanded && responses === null) {
      try { const { data } = await api.get(`/asks/${ask.id}/responses`); setResponses(data); }
      catch { setResponses([]); }
    }
    setExpanded((v) => !v);
  };

  const setStatus = async (status) => {
    try { await api.put(`/asks/${ask.id}`, { status }); onChanged(); }
    catch { toast.error("Could not update"); }
  };
  const remove = async () => {
    try { await api.delete(`/asks/${ask.id}`); toast.success("Ask deleted"); onChanged(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <Card className="rounded-xl border bg-card p-4" data-testid={`ask-row-${ask.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{ask.title}</span>
            <Badge variant="secondary" className="font-normal">{CATEGORIES[ask.category] || ask.category}</Badge>
            {ask.status === "fulfilled"
              ? <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">Fulfilled</Badge>
                      : <Badge className="border-brand/30 bg-brand/10 text-brand-ink">Open</Badge>}
          </div>
          {ask.description && <p className="mt-1 text-xs text-muted-foreground">{ask.description}</p>}
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={toggle} data-testid={`ask-responses-${ask.id}`}>
            {ask.responses_count} response{ask.responses_count === 1 ? "" : "s"} {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
          {ask.status === "open"
            ? <Button variant="ghost" size="sm" className="text-xs" onClick={() => setStatus("fulfilled")} data-testid={`ask-fulfill-${ask.id}`}>Mark fulfilled</Button>
            : <Button variant="ghost" size="sm" className="text-xs" onClick={() => setStatus("open")} data-testid={`ask-reopen-${ask.id}`}>Reopen</Button>}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={remove} data-testid={`ask-delete-${ask.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 space-y-2 border-t pt-3">
          {(responses || []).map((r) => (
            <div key={r.id} className="rounded-md bg-secondary/60 p-2.5 text-xs" data-testid={`ask-response-${r.id}`}>
              <div className="font-semibold text-foreground">{r.investor_name} <span className="font-normal text-muted-foreground">· {formatDate(r.created_at)}</span></div>
              <div className="mt-0.5 text-foreground">{r.message}</div>
            </div>
          ))}
          {(responses || []).length === 0 && <p className="text-xs text-muted-foreground">No investor responses yet.</p>}
        </div>
      )}
    </Card>
  );
}

function QuestionRow({ q, onChanged }) {
  const [answer, setAnswer] = useState(q.ai_draft || "");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  const aiDraft = async () => {
    setDrafting(true);
    try { const { data } = await api.post(`/questions/${q.id}/ai-draft`); setAnswer(data.ai_draft); }
    catch (err) { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "AI draft failed"); }
    finally { setDrafting(false); }
  };

  const send = async () => {
    if (!answer.trim()) return;
    setSending(true);
    try { await api.post(`/questions/${q.id}/answer`, { answer }); toast.success("Answer sent to investor"); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not send answer"); }
    finally { setSending(false); }
  };

  return (
    <Card className="rounded-xl border bg-card p-4" data-testid={`question-row-${q.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-foreground">{q.question}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">{q.investor_name} · {formatDate(q.created_at)}</div>
        </div>
        {q.status === "answered"
          ? <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">Answered</Badge>
          : <Badge className="border-amber-200 bg-amber-50 text-amber-800">Pending</Badge>}
      </div>
      {q.status === "answered" ? (
        <div className="mt-3 rounded-md bg-secondary/60 p-3 text-sm text-foreground">{q.answer}</div>
      ) : (
        <div className="mt-3 space-y-2">
          <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Write your answer, or let AI draft one from your updates and metrics…" rows={3} data-testid={`question-answer-input-${q.id}`} />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={aiDraft} disabled={drafting} data-testid={`question-ai-draft-${q.id}`}>
              {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI draft
            </Button>
            <Button size="sm" className="gap-1.5" onClick={send} disabled={sending || !answer.trim()} data-testid={`question-send-${q.id}`}>
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send answer
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function AsksQA() {
  const { company } = useAuth();
  const [asks, setAsks] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", category: "intro" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!company) return;
    try {
      const [a, q] = await Promise.all([
        api.get(`/companies/${company.id}/asks`),
        api.get(`/companies/${company.id}/questions`),
      ]);
      setAsks(a.data);
      setQuestions(q.data);
    } catch (e) { console.error("Failed to load asks/questions", e); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const createAsk = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/companies/${company.id}/asks`, form);
      toast.success("Ask published to your investors");
      setForm({ title: "", description: "", category: "intro" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create ask"); }
    finally { setSaving(false); }
  };

  const pending = questions.filter((q) => q.status === "pending").length;

  return (
    <AppShell title="Asks & Q&A" subtitle="Put your investors to work and answer their questions">
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <Tabs defaultValue="asks">
          <TabsList data-testid="asksqa-tabs">
            <TabsTrigger value="asks" data-testid="tab-asks">Asks ({asks.length})</TabsTrigger>
            <TabsTrigger value="qa" data-testid="tab-qa">Investor Q&A {pending > 0 ? `(${pending} pending)` : ""}</TabsTrigger>
          </TabsList>

          <TabsContent value="asks" className="mt-4">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[360px_1fr]">
              <Card className="h-fit rounded-xl border bg-card p-5">
                <div className="mb-3 flex items-center gap-2"><HandHelping className="h-4 w-4 text-primary" /><h2 className="text-sm font-semibold text-foreground">New ask</h2></div>
                <form onSubmit={createAsk} className="space-y-3">
                  <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Intro to fintech CFOs" required className="mt-1" data-testid="ask-form-title" /></div>
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                      <SelectTrigger className="mt-1" data-testid="ask-form-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Details</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} className="mt-1" data-testid="ask-form-desc" /></div>
                  <Button type="submit" disabled={saving} className="w-full gap-2" data-testid="ask-form-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Publish ask
                  </Button>
                </form>
                <p className="mt-3 text-xs text-muted-foreground">Open asks appear in every linked investor's portal. Investors respond with how they can help.</p>
              </Card>
              <div className="space-y-2">
                {asks.length === 0 ? (
                  <Card className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">No asks yet. Great founders make specific, actionable asks.</Card>
                ) : asks.map((a) => <AskRow key={a.id} ask={a} onChanged={load} />)}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="qa" className="mt-4 space-y-2">
            {questions.length === 0 ? (
              <Card className="rounded-xl border border-dashed p-10 text-center">
                <MessageCircleQuestion className="mx-auto h-8 w-8 text-muted-foreground" />
                <h2 className="mt-3 text-sm font-semibold text-foreground">No investor questions yet</h2>
                <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">When investors ask questions in their portal, they land here. AI can draft answers from your published updates and metrics.</p>
              </Card>
            ) : questions.map((q) => <QuestionRow key={q.id} q={q} onChanged={load} />)}
          </TabsContent>
        </Tabs>
      )}
    </AppShell>
  );
}
